// 使用浏览器的Web Crypto API实现的加密工具
// 作为Go WASM加密功能的替代方案

/**
 * 生成一个随机的初始化向量(IV)
 * @returns {Uint8Array} 12字节的IV
 */
export const generateIV = () => {
  const iv = new Uint8Array(12); // GCM模式使用12字节的IV
  window.crypto.getRandomValues(iv);
  return iv;
};

/**
 * 将字符串转换为Uint8Array
 * @param {string} str 要转换的字符串
 * @returns {Uint8Array} 转换后的二进制数据
 */
export const stringToBytes = (str) => {
  const encoder = new TextEncoder();
  return encoder.encode(str);
};

/**
 * 将Uint8Array转换为字符串
 * @param {Uint8Array} bytes 要转换的二进制数据
 * @returns {string} 转换后的字符串
 */
export const bytesToString = (bytes) => {
  const decoder = new TextDecoder();
  return decoder.decode(bytes);
};

/**
 * 将对象转换为Uint8Array
 * @param {Object} obj 要转换的对象
 * @returns {Uint8Array} 转换后的二进制数据
 */
export const objectToBytes = (obj) => {
  return stringToBytes(JSON.stringify(obj));
};

/**
 * 使用浏览器的SubtleCrypto API进行AES-GCM加密
 * @param {string|Uint8Array} message 要加密的消息
 * @param {Uint8Array} key 加密密钥
 * @param {Uint8Array} additionalData 附加验证数据
 * @returns {Promise<{ciphertext: Uint8Array, iv: Uint8Array}>} 包含密文和IV的对象
 */
export const encrypt = async (message, key, additionalData) => {
  try {
    // 确保消息是Uint8Array
    const messageBytes =
      message instanceof Uint8Array ? message : stringToBytes(String(message));

    // 确保additionalData是Uint8Array
    const aad =
      additionalData instanceof Uint8Array ? additionalData : new Uint8Array(0);

    // 生成随机IV
    const iv = generateIV();

    // 将密钥转换为CryptoKey对象
    const cryptoKey = await window.crypto.subtle.importKey(
      "raw",
      key,
      { name: "AES-GCM" },
      false,
      ["encrypt"]
    );

    // 执行加密
    const cipherBuffer = await window.crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv,
        additionalData: aad,
      },
      cryptoKey,
      messageBytes
    );

    // 将结果转换为Uint8Array
    const ciphertext = new Uint8Array(cipherBuffer);

    return { ciphertext, iv };
  } catch (error) {
    console.error("Web Crypto加密失败:", error);
    throw new Error("加密失败: " + error.message);
  }
};

/**
 * 使用浏览器的SubtleCrypto API进行AES-GCM解密
 * @param {Uint8Array} ciphertext 要解密的密文
 * @param {Uint8Array} key 解密密钥
 * @param {Uint8Array} additionalData 附加验证数据
 * @param {Uint8Array} iv 初始化向量
 * @returns {Promise<string>} 解密后的明文
 */
export const decrypt = async (ciphertext, key, additionalData, iv) => {
  try {
    // 确保所有参数是正确的类型
    if (!(ciphertext instanceof Uint8Array)) {
      throw new Error("密文必须是Uint8Array类型");
    }

    if (!(key instanceof Uint8Array)) {
      throw new Error("密钥必须是Uint8Array类型");
    }

    // 确保additionalData是Uint8Array
    const aad =
      additionalData instanceof Uint8Array ? additionalData : new Uint8Array(0);

    if (!(iv instanceof Uint8Array)) {
      throw new Error("IV必须是Uint8Array类型");
    }

    // 将密钥转换为CryptoKey对象
    const cryptoKey = await window.crypto.subtle.importKey(
      "raw",
      key,
      { name: "AES-GCM" },
      false,
      ["decrypt"]
    );

    // 执行解密
    const plainBuffer = await window.crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv,
        additionalData: aad,
      },
      cryptoKey,
      ciphertext
    );

    // 将结果转换为字符串
    const plaintext = bytesToString(new Uint8Array(plainBuffer));

    return plaintext;
  } catch (error) {
    console.error("Web Crypto解密失败:", error);
    throw new Error("解密失败: " + error.message);
  }
};

/**
 * 检查Web Crypto API是否可用
 * @returns {boolean} 是否支持Web Crypto API
 */
export const isWebCryptoSupported = () => {
  return window.crypto && window.crypto.subtle;
};
